import { Injectable, inject } from '@angular/core';
import { IPromise, IQService, ITimeoutService } from 'angular';
import _ from 'lodash';

import { ReportsGeneratorService } from './generator/generator.service';
import { Q_PORT, TIMEOUT_PORT } from '../utils';
import { TODO } from '@acme/b2b/types/core';

/**
 * ReportsService (Angular 18)
 *
 * Ported 1:1 from apps/b2b-ui/common/services/reports/reports.service.js
 * (legacy AngularJS name 'ReportsService'). Parses report result sets, computes
 * subtotals/group summaries, and delegates printable-markup generation to
 * ReportsGeneratorService.
 *
 * Dependency mapping:
 *  - ReportsGeneratorService → Angular 18, injected directly (internal
 *    sub-service, not downgrade-registered).
 *  - `$q`/`$timeout` → Q_PORT / TIMEOUT_PORT (shared fundamental ports in
 *    core/utils, wired in core.module.ts).
 *  - `angular.copy` → lodash `cloneDeep`.
 *
 * Registered back into AngularJS under 'ReportsService' via downgradeInjectable
 * in app/upgrade/ng-downgraded-components.ts. Wave 3 #16.
 */
@Injectable({ providedIn: 'root' })
export class ReportsService {
  private reportsGeneratorService = inject(ReportsGeneratorService);
  private $q = inject<IQService>(Q_PORT);
  private $timeout = inject<ITimeoutService>(TIMEOUT_PORT);

  metaFields: any[] = [];
  progress: any = {};
  partName = 'processing';

  init(config: any): boolean {
    if (config.progress) {
      this.progress = config.progress;
    }

    return true;
  }

  parse(data: any, fields: any[]): any[] {
    let i, ii;
    let j, jj;
    let field: TODO;
    const r: any[] = [];
    const cols = data.resultFieldList.length;

    if (cols > 0) {
      for (i = 0, ii = data.resultFieldList[0].values.length; i < ii; i++) {
        field = {};
        for (j = 0, jj = fields.length; j < jj; j++) {
          field[data.resultFieldList[j % cols].fieldName] = data.resultFieldList[j % cols].values[i];
        }
        r.push(field);
      }
    }

    return r;
  }

  summarize(fields: any[], summary: any): IPromise<any> {
    let rows: any[] = [];
    let groups: any = {};
    const q = this.$q.defer();

    this.progress.setValue(this.partName, 0);

    this.$timeout(() => {
      const o = this.summarizeFields(fields, summary.subTotals);
      rows = o.rows;
      groups = o.groups;

      this.progress.setValue(this.partName, 25);

      this.$timeout(() => {
        rows = this.summarizeSubtotals(rows, summary.subTotals);

        this.progress.setValue(this.partName, 50);

        this.$timeout(() => {
          groups = this.summarizeGroups(rows, groups);

          this.progress.setValue(this.partName, 75);

          q.resolve(groups);
          return true;
        }, 30);
        return true;
      }, 30);
      return true;
    }, 30);

    return q.promise;
  }

  summarizeFields(fields: any[], subTotals: any[]): { rows: any[]; groups: any } {
    let i, ii;
    let j, jj;
    let k, kk;
    let field;
    let subtotal;
    let groupKey;
    let fieldKey;
    let highlightWidth;
    let highlightColumns: TODO;
    const rows: any[] = [];
    const groups: any = {};

    const time = new Date().getTime();

    i = 0;
    ii = fields.length;
    while (i < ii) {
      rows.push(fields[i]);
      j = 0;
      jj = subTotals.length;
      while (j < jj) {
        if (i === subTotals[j].endIndex) {
          highlightWidth = 0;
          highlightColumns = {};

          subtotal = _.cloneDeep(fields.slice(i)[0]); // Copy last row

          for (field in subtotal) {
            if (Object.prototype.hasOwnProperty.call(subtotal, field)) {
              subtotal[field] = '';
            }
          }

          k = 0;
          kk = subTotals[j].subtotalFields.length;
          while (k < kk) {
            subtotal[subTotals[j].subtotalFields[k].name] = subTotals[j].subtotalFields[k].value;
            highlightColumns[subTotals[j].subtotalFields[k].name] = true;
            highlightWidth++;
            k += 1;
          }

          subtotal.fields = [];
          k = 0;
          kk = subTotals[j].groupFields.length;
          while (k < kk) {
            subtotal[subTotals[j].groupFields[k].name] = subTotals[j].groupFields[k].value;
            subtotal.fields.push(subTotals[j].groupFields[k].name);
            k += 1;
          }

          subtotal.subtotal = true;
          groupKey = subTotals[j].primaryGroupField.name + ':' + subTotals[j].primaryGroupField.value;
          fieldKey = subTotals[j].primaryGroupField.name + ':' + subtotal[subTotals[j].primaryGroupField.name];
          subtotal[subTotals[j].primaryGroupField.name] = '';
          if (!groups[groupKey]) {
            groups[groupKey] = {
              name: subTotals[j].primaryGroupField.name,
              value: subTotals[j].primaryGroupField.value,
              highlightColumns: highlightColumns,
              highlightWidth: highlightWidth,
              subtotals: 0,
              startIndex: rows.length,
              endIndex: 0,
              values: [],
            };
          }
          if (groupKey === fieldKey) {
            groups[groupKey].subtotals++;

            if (subTotals[j].startIndex < groups[groupKey].startIndex) {
              groups[groupKey].startIndex = subTotals[j].startIndex;
            }
            if (subTotals[j].endIndex > groups[groupKey].endIndex) {
              groups[groupKey].endIndex = subTotals[j].endIndex;
            }
          }
          rows.push(subtotal);
        }
        j += 1;
      }
      i += 1;
    }

    console.log(
      'summarizeFields Time:' + (new Date().getTime() - time) + 'ms ' + (new Date().getTime() - time) / 1000 + 's',
    );

    return {
      rows: rows,
      groups: groups,
    };
  }

  summarizeSubtotals(rows: any[], subTotals: any[]): any[] {
    let i, ii;
    let j, k;
    let name;
    const lastSubgroup: any = {};
    let subgroup = 0;

    const time = new Date().getTime();

    // Visual cleanup for layout

    j = subTotals.length || 1;
    while ((j -= 1)) {
      k = subTotals[j].groupFields.length || 1;
      while ((k -= 1)) {
        lastSubgroup[subTotals[j].groupFields[k].name] = '';
      }
    }

    for (name in lastSubgroup) {
      if (Object.prototype.hasOwnProperty.call(lastSubgroup, name)) {
        i = 0;
        ii = rows.length;
        while (i < ii) {
          if (lastSubgroup[name] !== rows[i][name]) {
            lastSubgroup[name] = rows[i][name];
          } else {
            rows[i][name] = '';
          }
          i += 1;
        }
      }
      subgroup += 1;
    }

    console.log(
      'summarizeSubtotals Time:' + (new Date().getTime() - time) + 'ms ' + (new Date().getTime() - time) / 1000 + 's ',
    );

    return rows;
  }

  summarizeGroups(rows: any[], groups: any): any {
    let i, ii;
    let key;
    let field;
    let lastEndIndex = 0;
    let groupLength = 0;

    const time = new Date().getTime();

    for (key in groups) {
      if (Object.prototype.hasOwnProperty.call(groups, key)) {
        console.log('ReportsService::summarize key', key);
        console.log(
          'ReportsService::summarize groups',
          key,
          lastEndIndex,
          groups[key].startIndex + groups[key].endIndex + groups[key].subtotals + 1,
        );
        groupLength = groups[key].endIndex - groups[key].startIndex + groups[key].subtotals + 1;
        groups[key].values = rows.slice(lastEndIndex, lastEndIndex + groupLength);
        lastEndIndex = lastEndIndex + groupLength;
        console.log(
          'ReportsService::summarize groups',
          'length',
          groupLength,
          groups[key].values.length,
          'start',
          groups[key].startIndex,
          'end',
          groups[key].endIndex,
          'sub',
          groups[key].subtotals,
        );
        console.log('ReportsService::summarize groups', groups[key]);
        for (i = 0, ii = groups[key].values.length; i < ii; i++) {
          if (groups[key].values[i].subtotal) {
            //console.log('ReportsService::summarize groups',  groups[key].values[i], groups[key].values[i].fields[groups[key].values[i].fields.length - 1]);

            for (field in groups[key].values[i]) {
              if (Object.prototype.hasOwnProperty.call(groups[key].values[i], field)) {
                if (groups[key].values[i].fields.indexOf(field) === -1) {
                  groups[key].values[i][field] = 'Subtotal';
                  break;
                }
              }
            }
          }
        }
      }
    }

    console.log(
      'summarizeGroups Time:' + (new Date().getTime() - time) + 'ms ' + (new Date().getTime() - time) / 1000 + 's',
    );

    return groups;
  }

  generate(props: any): IPromise<any> {
    let r;
    const q = this.$q.defer();

    this.reportsGeneratorService.init({
      progress: this.progress,
      type: props.type,
    });

    this.reportsGeneratorService.generate(props.report).then((markup: string) => {
      r = markup;

      this.progress.setValue(this.partName, 100);
      q.resolve(r);
    });

    return q.promise;
  }

  getColumns(data: any[]): any[] {
    let i, ii;
    let field: TODO;
    let column: TODO;
    const columns: any[] = [];

    for (i = 0, ii = data.length; i < ii; i++) {
      field = this.findMetadataField(data[i].fieldName).field;

      column = {
        field: field.name,
        displayName: field.displayName,
      };

      column.type = field.dataType.toLowerCase();

      columns.push(column);
    }

    return columns;
  }

  getTotals(totals: any[]): any {
    let i;
    let ii = totals.length;
    const r: any = ii > 0 ? {} : false;

    for (i = 0, ii = totals.length; i < ii; i++) {
      r[totals[i].name] = totals[i].value;
    }

    return r;
  }

  isDateField(name: string): boolean {
    return false;
  }

  isFieldType(name: string, type: string): boolean {
    let i, ii;
    let r = false;
    const fields = this.metaFields;

    for (i = 0, ii = fields.length; i < ii; i++) {
      if (fields[i].name.toLowerCase() === name.toLowerCase()) {
        r = fields[i].dataType.toLowerCase() === type.toLowerCase();
        break;
      }
    }

    return r;
  }

  findGroupField(name: string, queryExpression: any): any {
    let i, ii;
    let r: any = false;

    console.log('getGroupField', name, queryExpression);

    for (i = 0, ii = queryExpression.groupFields.length; i < ii; i++) {
      if (queryExpression.groupFields[i].fieldName === name) {
        r = {
          index: i,
          field: queryExpression.groupFields[i],
        };
        break;
      }
    }

    return r;
  }

  findMetadataField(name: string): any {
    let i, ii;
    let r: any = false;
    const fields = this.metaFields;

    for (i = 0, ii = fields.length; i < ii; i++) {
      if (fields[i].name.toLowerCase() === name.toLowerCase()) {
        r = {
          index: i,
          field: fields[i],
        };
        break;
      }
    }

    return r;
  }

  findMetadataFieldsByType(type: any, prop?: string): any {
    let i, ii;
    let j, jj;
    let types: any[] = [];
    let r: any = false;
    const fields = this.metaFields;

    if (type instanceof Array) {
      types = type;
    } else if (typeof type === 'string') {
      types.push(type);
    }

    for (i = 0, ii = fields.length; i < ii; i++) {
      for (j = 0, jj = types.length; j < jj; j++) {
        if (fields[i].dataType.toLowerCase() === types[j].toLowerCase()) {
          if (r === false) {
            r = [];
          }
          if (prop && Object.prototype.hasOwnProperty.call(fields[i], prop)) {
            // Get only the property
            r.push(fields[i][prop]);
          } else {
            //Get the entire field
            r.push(fields[i]);
          }
          break;
        }
      }
    }

    return r;
  }
}
